import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  debounce, isEmpty,
} from 'lodash';
import {
  PostAdd, Search, AssignmentInd, ImageSearchRounded,
} from '@mui/icons-material';
import { TextField, Avatar, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TagValueIcon } from 'components/TagValueSearch/TagValueIcon';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { userIsAdmin } from 'lib/helpers';
import { ProfileUser } from 'lib/types/users';
import { FilterResult } from 'lib/types/filters';
import { TAG_TYPE_IDS, DEBOUNCE_TIME } from 'lib/constants';
import {
  clearQuery, defaultFilterKeys, toSlug, updateRouterQuery,
} from 'lib/utils/filterUtils';
import {
  filterOptions, formatProfileFilterResults, formatUserResult, MINIMUM_INPUT_LENGTH,
} from './utils';

const defaultLabel = 'Search creators/companies, tags, events, keywords';
interface Props {
  filters: (FilterResult|string)[];
  label?: string;
  profileUser: ProfileUser;
  useNaturalLanguage?: boolean;
}

export const ProfileMultiFilter = ({
  filters,
  label,
  profileUser,
  useNaturalLanguage = false,
}: Props) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [userOptions, setUserOptions] = useState<FilterResult[]>([]);
  const [options, setOptions] = useState<FilterResult[]>([]);
  const [isCriteriaLoading, setIsCriteriaLoading] = useState(false);

  const isFetching = useRef(false);
  const API = useApi();
  const currentUser = useCurrentUser();

  const filterPosts = useMemo(
    () => debounce(async (input, callback) => {
      try {
        // If user type the symbol, don't search yet
        if (input === '#' || input === '@') {
          return null;
        }

        // If the input has not enough characters, just return the keyword option
        if ((input || '').length < MINIMUM_INPUT_LENGTH) {
          callback([]);
        }

        const searchResults = await API.getProfileFilterSearch(input, profileUser.id);
        const formattedResults = formatProfileFilterResults(searchResults);
        callback(formattedResults);
        return null;
      } catch (error: any) {
        isFetching.current = false;
        return null;
      }
    }, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    isFetching.current = true;
    let active = true;
    if (isEmpty(inputValue)) {
      setOptions([]);
      return undefined;
    }

    filterPosts(inputValue, (results: FilterResult[]) => {
      isFetching.current = false;
      if (active) {
        const keyword: FilterResult = {
          id: 0,
          label: inputValue,
          subLabel: 'keyword',
          query: 'keyword',
          relevancyScore: 1,
        };

        const criteriaSearch: FilterResult = {
          id: 2,
          label: `Posts matching "${inputValue}"`,
          subLabel: 'Explore posts',
          query: 'criteriaSearch',
          relevancyScore: 0,
        };
        const baseOptions = [keyword];

        if (useNaturalLanguage && userIsAdmin(currentUser)) {
          baseOptions.push(criteriaSearch);
        }

        if (results) {
          if (inputValue.charAt(0) !== '@' && inputValue.charAt(0) !== '#') {
            setOptions([
              ...results,
              ...baseOptions,
            ]);
            return;
          }
          setOptions(results);
          return;
        }
        setOptions(baseOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue]);

  useEffect(() => {
    const collab = profileUser.collaborators.map((c) => formatUserResult({
      id: c.id,
      model: 'users',
      score: 1,
      avatarUrl: c.avatarUrl,
      name: c.name,
      username: c.username,
      isLocal: true,
    }));
    setUserOptions(collab);
  }, [profileUser]);

  const renderOptionImg = (option: FilterResult) => {
    switch (option.query) {
    case 'users':
      return (<Avatar alt={option.label} src={option.imgUrl} />);
    case 'tags':
      return (<div className="mr-6"><TagValueIcon tagTypeId={TAG_TYPE_IDS.tag} fontSize="large" color="secondary" /></div>);
    case 'events':
      return (<div className="mr-6"><TagValueIcon tagTypeId={TAG_TYPE_IDS.event} fontSize="large" color="secondary" /></div>);
    case 'postingAccount':
      return (<div className="mr-6"><PostAdd fontSize="large" color="secondary" /></div>);
    case 'role':
      return (<div className="mr-6"><AssignmentInd fontSize="large" color="secondary" /></div>);
    case 'criteriaSearch':
      return (<div className="mr-6"><ImageSearchRounded fontSize="large" color="secondary" /></div>);
    default:
      return (<Search />);
    }
  };

  const renderSelectedLabel = (option: FilterResult|string) => {
    if (typeof option === 'string') {
      return `keyword: ${option}`;
    }

    switch (option.query) {
    case 'users':
      return `@${option.label}`;
    case 'keyword':
      return `keyword: ${option.label}`;
    case 'postingAccount':
      return `account: ${option.label}`;
    case 'role':
      return `role: ${option.label}`;
    default: {
      return `${option.subLabel.substring(0, option.subLabel.length - 1)}: ${option.label}`;
    }
    }
  };

  const onChange = async (values: (FilterResult|string)[]) => {
    if (values.find((v) => (v as FilterResult)?.query === 'criteriaSearch')) {
      try {
        setIsCriteriaLoading(true);
        const response = await API.getPostSearchCriteria(inputValue);
        updateRouterQuery(router, response.params);
        return;
      } catch (error) {
        console.error('Error fetching post search criteria', error);
      } finally {
        setIsCriteriaLoading(false);
      }
      return;
    }

    const buildMultiQuery = () => values.reduce(
      (acc, value) => {
        if (typeof value === 'string') {
          acc.keyword = value;
          return acc;
        }

        const key = value.query || 'keyword';
        const slug = toSlug(value.label, value.id);
        if (!acc[key]) {
          acc[key] = [slug];
        } else {
          acc[key].push(slug);
        }

        return acc;
      }, {} as any,
    );

    const query = {
      ...clearQuery(defaultFilterKeys),
      ...buildMultiQuery(),
    };
    updateRouterQuery(router, query);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      loading={!!inputValue?.length && (isFetching.current || isCriteriaLoading)}
      value={filters}
      filterOptions={filterOptions}
      className="mx-auto"
      autoHighlight
      getOptionLabel={(option) => renderSelectedLabel(option)}
      options={[...userOptions, ...options]}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue.trim());
      }}
      disabled={isCriteriaLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={label || defaultLabel}
          fullWidth
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <div className="absolute text-mediumgray dark:text-hintgray right-4 top-3">
                {isCriteriaLoading ? <CircularProgress size={20} /> : null}
              </div>
            ),
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && (isFetching.current || isCriteriaLoading)) {
              e.stopPropagation();
            }
          }}
        />
      )}
      //@ts-ignore
      renderOption={
        (props, option: FilterResult) => (
          <li {...props} key={option.id}>
            <div className="flex w-full">
              <div className="w-20">
                {renderOptionImg(option)}
              </div>
              <div className="w-4/5">
                <div className="font-bold">
                  {option.label}
                </div>
                <div className="text-mediumgray dark:text-hintgray text-sm">
                  {option.subLabel}
                </div>
              </div>
            </div>
          </li>
        )
      }
      size="small"
    />
  );
};
